import React from "react";
import { Box, Button, Paper, Typography } from "@mui/material";
import {
  ChevronRight as ExpandRightIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";

import type { EnrichedStoreBlock } from "../../services/distributionPreviewService";
import { ActiveLinesBySupplierPanel } from "./ActiveLinesBySupplierPanel";
import { DeferredBySupplierPanel } from "./DeferredBySupplierPanel";

/**
 * Tabla de líneas enriquecidas por tienda con checkbox de exclusión y
 * sección colapsable de líneas diferidas por capacity-cap. Extraído de
 * DistribucionPreviewPage para cumplir P7 (max 500 líneas por archivo).
 */
export function PreviewStoreTable({
  block,
  excludedSet,
  onToggle,
  deferredOpen,
  onToggleDeferred,
  onRepromoteDeferred,
}: {
  block: EnrichedStoreBlock;
  excludedSet: Set<string>;
  onToggle: (recId: string) => void;
  deferredOpen: boolean;
  onToggleDeferred: () => void;
  onRepromoteDeferred?: (ids: string[]) => Promise<void>;
}): React.ReactElement {
  const selectedCount = block.lines.filter(
    (l) => !excludedSet.has(l.recommendation_id),
  ).length;
  const selectedUnits = block.lines.reduce(
    (acc, l) =>
      excludedSet.has(l.recommendation_id) ? acc : acc + l.qty_planned,
    0,
  );
  const insufficientStockCount = block.lines.filter(
    (l) => !l.stock_suficiente,
  ).length;

  const sortedLines = React.useMemo(
    () => [...block.lines].sort((a, b) => b.qty_planned - a.qty_planned),
    [block.lines],
  );
  // Auditoría 2026-05-05: backend ya entrega `block.deferred` agrupado
  // por proveedor (DeferredLinesBySupplier[]) ordenado por total_units DESC.
  // El componente DeferredBySupplierPanel se encarga de la UI dedicada.
  const totalDeferredLines = React.useMemo(
    () => block.deferred.reduce((acc, b) => acc + b.total_lines, 0),
    [block.deferred],
  );

  return (
    <Paper variant="outlined">
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          Tienda {block.store_mxp_code}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {selectedCount} / {block.lines.length} líneas · {selectedUnits} unidades
          {insufficientStockCount > 0 && (
            <Typography component="span" variant="caption" color="warning.main">
              {" "}· {insufficientStockCount} líneas sin stock suficiente
            </Typography>
          )}
          {block.deferred.length > 0 && (
            <> · {block.deferred.length} diferidas por capacidad</>
          )}
        </Typography>
      </Box>

      {block.lines.length > 0 && (
        <Box sx={{ p: 2 }}>
          <ActiveLinesBySupplierPanel
            lines={block.lines}
            excludedSet={excludedSet}
            onToggle={onToggle}
          />
        </Box>
      )}

      {block.deferred.length > 0 && (
        <Box sx={{ borderTop: "1px solid", borderColor: "divider" }}>
          <Button
            size="small"
            startIcon={
              deferredOpen ? <ExpandMoreIcon /> : <ExpandRightIcon />
            }
            onClick={onToggleDeferred}
            sx={{ m: 1 }}
          >
            {totalDeferredLines} líneas diferidas por capacidad —{" "}
            {block.deferred.length} proveedor
            {block.deferred.length === 1 ? "" : "es"}
          </Button>
          {deferredOpen && (
            <Box sx={{ p: 2 }}>
              <DeferredBySupplierPanel
                buckets={block.deferred}
                onRepromote={async (ids) => {
                  if (onRepromoteDeferred) {
                    await onRepromoteDeferred(ids);
                  }
                }}
              />
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
}

// Auditoría 2026-05-05 — el agrupamiento por categoría
// (`DeferredSummary` + `groupDeferred` + `DeferredGroup`) fue removido.
// La UI ahora muestra las diferidas agrupadas por proveedor en
// `DeferredBySupplierPanel`, alineado con almacén/logística que opera
// por proveedor (no por categoría).
